import { format } from "date-fns";
import { cn } from "../cn";
import { Pill } from "./Pill";
import type { Milestone, MilestoneStatus } from "@/domain/types";

const STATUS_META: Record<MilestoneStatus, { label: string; variant: React.ComponentProps<typeof Pill>["variant"] }> = {
  planned: { label: "Planned", variant: "neutral" },
  in_progress: { label: "In progress", variant: "info" },
  evidence_submitted: { label: "Evidence submitted", variant: "warn" },
  ready_for_disbursement: { label: "Ready for disbursement", variant: "brand" },
  disbursed: { label: "Disbursed", variant: "success" },
  closed: { label: "Closed", variant: "neutral" },
};

export function MilestoneStatusPill({ status }: { status: MilestoneStatus }) {
  const m = STATUS_META[status];
  return <Pill variant={m.variant}>{m.label}</Pill>;
}

export function MilestoneStrip({
  milestones,
  variant = "compact",
  onSelect,
  className,
}: {
  milestones: Milestone[];
  variant?: "compact" | "full";
  onSelect?: (m: Milestone) => void;
  className?: string;
}) {
  return (
    <div className={cn("flex gap-3 overflow-x-auto pb-1", className)}>
      {milestones.map((m) => {
        const meta = STATUS_META[m.status];
        return (
          <button
            key={m.id}
            onClick={onSelect ? () => onSelect(m) : undefined}
            className={cn(
              "flex w-56 shrink-0 flex-col justify-between rounded-md border bg-card p-3 text-left transition-colors",
              variant === "full" ? "h-40" : "h-32",
              m.current ? "border-2 border-brand" : "border-line",
              onSelect && "hover:border-strong",
            )}
          >
            <div>
              <p className="text-sm font-medium text-ink">{m.name}</p>
              {variant === "full" && m.description && (
                <p className="mt-1 line-clamp-2 text-xs text-faint">{m.description}</p>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <span className="scale-90 origin-left">
                  <Pill variant={meta.variant}>{meta.label}</Pill>
                </span>
                <span className="text-xs tabular-nums text-faint">
                  {format(new Date(m.targetDate), "d MMM")}
                </span>
              </div>
              {/* Share-of-budget is a proportion of the whole, not progress — render it
                  as text, never a progress bar (a filled bar reads as "done"). */}
              <span className="text-xs tabular-nums text-faint">
                {Math.round(m.budgetSharePct * 100)}% of budget
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );
}
